import React, { useState } from "react";
import {
  Route,
  ArrowRight,
  Users,
  Check,
  ChevronDown,
} from "lucide-react";
import { POPULAR_CAMPUS_HUBS } from "@/constants/locations";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface RouteSuggestion {
  from: string;
  to: string;
  count: number;
}

interface TopRequestedRoutesProps {
  routes: RouteSuggestion[];
  currentFrom?: string;
  currentTo?: string;
  onSelectRoute: (route: RouteSuggestion) => void;
  className?: string;
}

/**
 * Intelligently resolves raw location strings into clean, readable campus/city hub labels
 * while preserving the raw address for form values and tooltip inspection.
 */
export function getFriendlyLocationName(rawAddress: string): string {
  if (!rawAddress) return "";
  const trimmed = rawAddress.trim();
  const lower = trimmed.toLowerCase();

  // 1. Direct match with defined campus hubs
  const hubMatch = POPULAR_CAMPUS_HUBS.find((hub) => {
    const hubAddr = hub.address.toLowerCase();
    const hubLbl = hub.label.toLowerCase();
    return (
      lower === hubAddr ||
      lower === hubLbl ||
      hubAddr.includes(lower) ||
      lower.includes(hubAddr) ||
      (lower.includes("manipal") && hub.id === "muj") ||
      (lower.includes("airport") && hub.id === "airport") ||
      ((lower.includes("railway") || lower.includes("junction")) &&
        hub.id === "junction") ||
      (lower.includes("sindhi camp") && hub.id === "sindhicamp") ||
      ((lower.includes("wtp") || lower.includes("world trade park")) &&
        hub.id === "wtp") ||
      (lower.includes("vaishali") && hub.id === "vaishali")
    );
  });

  if (hubMatch) return hubMatch.label;

  // 2. Intelligent landmark parsing for arbitrary geocoded places
  const segments = trimmed.split(",").map((s) => s.trim()).filter(Boolean);
  if (segments.length > 0 && segments[0]) {
    const first = segments[0];
    if (first.length <= 26) return first;
    return first.slice(0, 23) + "...";
  }

  return trimmed.length > 26 ? trimmed.slice(0, 23) + "..." : trimmed;
}

export const TopRequestedRoutes: React.FC<TopRequestedRoutesProps> = ({
  routes,
  currentFrom = "",
  currentTo = "",
  onSelectRoute,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  // Filter out any invalid routes where source and destination are identical or contain invalid characters/gibberish
  const validRoutes = (routes || []).filter((route) => {
    const from = route.from?.trim();
    const to = route.to?.trim();
    if (!from || !to) return false;
    if (from.toLowerCase() === to.toLowerCase()) return false;

    // Disallow characters that do not belong in place names ($, ?, !, #, etc.)
    const validPlaceRegex = /^[a-zA-Z0-9\s,.'\-()]+$/;
    if (!validPlaceRegex.test(from) || !validPlaceRegex.test(to)) return false;

    // Must contain letters
    if (!/[a-zA-Z]{2,}/.test(from) || !/[a-zA-Z]{2,}/.test(to)) return false;

    // Disallow unnatural consonant sequences
    if (/\b[bcdfghjklmnpqrstvwxyz]{5,}\b/i.test(from) || /\b[bcdfghjklmnpqrstvwxyz]{5,}\b/i.test(to)) return false;

    return true;
  });

  if (validRoutes.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50/50 via-white to-white p-3 shadow-xs transition-all duration-200",
        className
      )}
    >
      {/* Header bar */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between text-left cursor-pointer group focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-500/40 rounded-md"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-md bg-amber-100/90 text-amber-700 flex items-center justify-center shrink-0 border border-amber-200/70 shadow-2xs">
            <Route className="w-3.5 h-3.5 text-amber-700" />
          </div>

          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="text-xs font-semibold text-zinc-900 tracking-tight">
              Top Requested Routes
            </span>

            {/* Dynamic live badge */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100/80 text-amber-900 border border-amber-200">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500" />
              </span>
              {validRoutes.length} {validRoutes.length === 1 ? "Route Available" : "Routes Available"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-zinc-400 group-hover:text-zinc-700 transition-colors shrink-0 ml-2">
          <span className="text-[10px] font-medium hidden sm:inline select-none">
            {isOpen ? "Hide" : "Show"}
          </span>
          <ChevronDown
            className={cn(
              "w-4 h-4 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </button>

      {/* Expandable routes list */}
      {isOpen && (
        <div className="mt-2.5 pt-2 border-t border-amber-100 space-y-2 animate-in fade-in-50 duration-150">
          <div className="flex items-center justify-between px-0.5">
            <span className="text-[10px] font-medium text-zinc-500 tracking-tight">
              Frequent requests by campus commuters. Click to auto-fill:
            </span>
          </div>

          <div className="space-y-1.5">
            {validRoutes.map((route, index) => {
              const isSelected =
                Boolean(currentFrom?.trim()) &&
                Boolean(currentTo?.trim()) &&
                currentFrom.trim().toLowerCase() === route.from.trim().toLowerCase() &&
                currentTo.trim().toLowerCase() === route.to.trim().toLowerCase();

              const fromLabel = getFriendlyLocationName(route.from);
              const toLabel = getFriendlyLocationName(route.to);

              return (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onSelectRoute(route)}
                      className={cn(
                        "group relative w-full flex items-center justify-between gap-2 p-2 rounded-lg border text-left transition-all duration-150 cursor-pointer select-none",
                        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-500/40",
                        isSelected
                          ? "bg-amber-50/95 border-amber-400 ring-1 ring-amber-400/30 shadow-2xs font-medium"
                          : "bg-white hover:bg-amber-50/40 border-zinc-200/90 hover:border-amber-300 shadow-[0_1px_2px_rgba(0,0,0,0.02)] hover:shadow-2xs active:scale-[0.99]"
                      )}
                    >
                      {/* Route track */}
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        {/* Origin */}
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 ring-2 ring-emerald-100"
                            aria-hidden="true"
                          />
                          <span
                            className={cn(
                              "text-xs tracking-tight truncate max-w-[95px] sm:max-w-[130px]",
                              isSelected
                                ? "font-semibold text-zinc-900"
                                : "font-medium text-zinc-700 group-hover:text-zinc-900"
                            )}
                          >
                            {fromLabel}
                          </span>
                        </div>

                        {/* Flow arrow */}
                        <div className="flex items-center justify-center shrink-0 px-0.5 text-zinc-400 group-hover:text-amber-600 transition-colors">
                          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                        </div>

                        {/* Destination */}
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 ring-2 ring-amber-100"
                            aria-hidden="true"
                          />
                          <span
                            className={cn(
                              "text-xs tracking-tight truncate max-w-[95px] sm:max-w-[130px]",
                              isSelected
                                ? "font-semibold text-zinc-900"
                                : "font-medium text-zinc-700 group-hover:text-zinc-900"
                            )}
                          >
                            {toLabel}
                          </span>
                        </div>
                      </div>

                      {/* Demand count & selection indicator */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors",
                            isSelected
                              ? "bg-amber-200/90 text-amber-900"
                              : "bg-zinc-100 text-zinc-700 border border-zinc-200/80 group-hover:bg-amber-100/80 group-hover:text-amber-900 group-hover:border-amber-200"
                          )}
                        >
                          <Users className="w-3 h-3 text-zinc-500 group-hover:text-amber-700 shrink-0" />
                          <span>
                            {route.count}{" "}
                            <span className="font-normal text-[10px] opacity-80">
                              {route.count === 1 ? "rider" : "riders"}
                            </span>
                          </span>
                        </span>

                        {isSelected && (
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white shadow-2xs shrink-0">
                            <Check className="w-2.5 h-2.5 stroke-[3]" />
                          </span>
                        )}
                      </div>
                    </button>
                  </TooltipTrigger>

                  <TooltipContent
                    side="top"
                    align="center"
                    className="bg-zinc-900 text-white border-zinc-800 p-2.5 shadow-xl max-w-xs space-y-1.5"
                  >
                    <div className="flex items-center justify-between gap-2 border-b border-zinc-800 pb-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 flex items-center gap-1">
                        <Route className="w-3 h-3 text-amber-400" />
                        Popular Campus Route
                      </span>
                      <span className="text-[10px] text-zinc-400 font-medium">
                        {route.count}{" "}
                        {route.count === 1 ? "rider requesting" : "riders requesting"}
                      </span>
                    </div>

                    <div className="text-xs space-y-1 font-normal">
                      <div className="flex items-start gap-1.5">
                        <span className="text-emerald-400 font-bold shrink-0 mt-0.5">
                          ●
                        </span>
                        <div className="leading-tight">
                          <span className="text-zinc-400 text-[10px] uppercase font-semibold">
                            Pickup:{" "}
                          </span>
                          <span className="text-zinc-200">{route.from}</span>
                        </div>
                      </div>

                      <div className="flex items-start gap-1.5">
                        <span className="text-amber-400 font-bold shrink-0 mt-0.5">
                          ●
                        </span>
                        <div className="leading-tight">
                          <span className="text-zinc-400 text-[10px] uppercase font-semibold">
                            Drop-off:{" "}
                          </span>
                          <span className="text-zinc-200">{route.to}</span>
                        </div>
                      </div>
                    </div>

                    <div className="text-[10px] text-amber-300/90 pt-1 font-medium border-t border-zinc-800">
                      Click to auto-populate pickup and drop-off
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopRequestedRoutes;
